import { Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigationType } from "react-router";
import { useAuth } from "@/app/auth";
import { Drawer, EmptyState, Skeleton, Spinner, Button } from "@/components/ui";
import { Sidebar, SidebarContent } from "./sidebar";
import { Topbar } from "./topbar";
import { CommandPalette } from "./command-palette";
import { AssistantDrawer } from "@/features/ai/assistant-drawer";
import { ASSISTANT_EVENT } from "@/lib/assistant";

export function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner className="size-7" />
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[1680px] space-y-5 px-4 py-6 sm:px-6 lg:px-7">
      <Skeleton className="h-9 w-64" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[112px]" />
        ))}
      </div>
      <Skeleton className="h-[320px]" />
    </div>
  );
}

/** Requires a session; used by full-screen authenticated pages outside the shell (site editor). */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!me) return <Navigate to="/connexion" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

export function AppShell() {
  const { me, loading, logout } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  useEffect(() => setNavOpen(false), [location.pathname]);

  // A new page always opens at the top (phones especially). Back / forward keep the browser behaviour, and query
  // string changes (tabs, filters) keep the position. React Router ScrollRestoration is not used: every fresh page
  // load shares the "default" history key, so it restored the previous page offset after a reload.
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  useEffect(() => {
    const open = () => setAssistantOpen(true);
    window.addEventListener(ASSISTANT_EVENT, open);
    return () => window.removeEventListener(ASSISTANT_EVENT, open);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) return <FullPageLoader />;
  if (!me) return <Navigate to="/connexion" replace state={{ from: location.pathname + location.search }} />;
  if (!me.current) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <EmptyState
          title="Aucun client accessible"
          description="Votre compte n'est rattaché à aucun client actif. Demandez une invitation au propriétaire de l'organisation."
          action={
            <Button variant="secondary" onClick={() => void logout()}>
              Se déconnecter
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-dvh">
      <Sidebar className="hidden lg:flex" />
      <div className="flex min-h-dvh min-w-0 flex-col lg:pl-[240px]">
        <Topbar onOpenNav={() => setNavOpen(true)} onOpenSearch={() => setSearchOpen(true)} />
        <main id="contenu" className="min-w-0 flex-1">
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <Drawer open={navOpen} onClose={() => setNavOpen(false)} side="left" width="w-[284px]" hideHeader className="bg-sidebar">
        <SidebarContent onNavigate={() => setNavOpen(false)} />
      </Drawer>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
}
